import { getActiveTokenScheme } from '@dashhudson/dashing-ui/utils/tokens';

/**
 * Reads dash-web's own colour custom properties at paint time.
 *
 * A colour imported from `ux/colours.js` is frozen for the life of the page, so
 * anything painted from it — canvas pixels especially — can never follow a
 * colour scheme change. This resolves the live value off the DOM instead, and
 * falls back to the frozen constant when there is no document (Node tests), no
 * `getComputedStyle`, or the property resolves empty (vitest loads no CSS, and
 * a property may simply not be declared).
 *
 * Deliberately Vue-free. Reactivity belongs to whoever builds the config —
 * see `useChartColours()`; this only reads.
 */

/**
 * Cache keyed by resolution root, then `scheme|property`.
 *
 * Keyed per root because dashing-ui's dark selector is bare: a `[data-theme]`
 * subtree resolves different values from the document element, and a flat cache
 * would let two charts in differently-themed subtrees collide. `WeakMap` so a
 * detached root cannot leak.
 */
let cache = new WeakMap();

const cacheFor = (root) => {
  let entry = cache.get(root);
  if (!entry) {
    entry = new Map();
    cache.set(root, entry);
  }
  return entry;
};

/**
 * The scheme the document is currently rendering.
 *
 * Taken from dashing-ui rather than read off the attribute: `useColorScheme()`
 * writes the attribute in a `flush: 'post'` watcher and updates this
 * immediately after, so a config rebuilt during render would otherwise key its
 * cache on the scheme it is replacing.
 */
const activeScheme = () => getActiveTokenScheme();

/**
 * Resolve one custom property to a concrete colour.
 *
 * @param {string} property CSS custom property, e.g. `--chart-set-01`
 * @param {string} fallback value to use when the DOM cannot answer
 * @param {{ el?: Element | null }} [options] element to resolve against, for a themed subtree
 * @returns {string}
 */
export function resolveColour(property, fallback, { el } = {}) {
  // `document` alone is the wrong probe: a test environment can define
  // `document` while leaving `getComputedStyle` undefined, and that is the API
  // this actually needs.
  const root = el ?? (typeof document === 'undefined' ? null : document.documentElement);
  if (!root || typeof getComputedStyle !== 'function') return fallback;

  const key = `${activeScheme()}|${property}`;
  const entry = cacheFor(root);
  const cached = entry.get(key);
  if (cached !== undefined) return cached;

  const live = getComputedStyle(root).getPropertyValue(property).trim();
  const value = live || fallback;
  entry.set(key, value);
  return value;
}

/**
 * Drops every cached read.
 *
 * The cache is module state keyed by root and scheme, so a test that stubs
 * `getComputedStyle` differently inherits whatever an earlier test resolved
 * against `document.documentElement`.
 *
 * @internal
 */
export function _resetColourCacheForTesting() {
  cache = new WeakMap();
}
